import type { MouseEventHandler } from 'react'
import React, { useMemo, useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import CurrencyContext from 'contexts/currencyContext'

import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineShieldSlashIcon from 'LuxKit/Icons/line/LineShieldSlashIcon'
import SolidShieldCheckIcon from 'LuxKit/Icons/solid/SolidShieldCheckIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import HelpPane from 'components/Common/HelpPane'
import Modal from 'LuxKit/Modal/Modal'
import TextLink from 'LuxKit/TextLink'

import { getRefundStatus, Refundable, generateRefundablePolicy, getRefundDescriptions } from '../utils/getCancellationPolicyStatus'
import ModalContext from 'contexts/ModalContext'
import Divider from 'LuxKit/Divider'

const TermsGrid = styled.div`
  display: grid;
  gap: ${rem(16)};
  grid-template-columns: 1fr 2fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }
`

interface Props {
  cancellationPolicies: Array<App.RoomRateCancellationPolicy>
  offerType: string
}

function StaticConnectionCancellationPoliciesComponent(props: Props) {
  const intl = useIntl()
  const currencyCode = useContext(CurrencyContext)
  const { cancellationPolicies, offerType } = props

  const refundable = useMemo<Refundable | null>(() => getRefundStatus(cancellationPolicies), [cancellationPolicies])
  const refundDescriptions = useMemo(() => getRefundDescriptions(offerType, intl), [offerType, intl])
  const nonRefundablePolicy = useMemo(() => cancellationPolicies.find(policy => (policy.policy.unit === 'percentage' && policy.policy.value === 100) || policy.nonRefundable), [cancellationPolicies])
  const simplifiedPolicies = useMemo(() => cancellationPolicies.map(policy => ({
    policy: {
      leadDaysMin: policy.policy.leadDaysMin,
      value: policy.policy.value,
      unit: policy.policy.unit,
    },
    nonRefundable: policy.nonRefundable,
  })), [cancellationPolicies])

  const refundablePolicy = useMemo(() => generateRefundablePolicy(cancellationPolicies), [cancellationPolicies])
  // Order policies by leadDaysMin, remove non-refundable policy if it exists, and add refundable policy if it exists
  const orderedPolicies = useMemo(() => {
    const policies = [...simplifiedPolicies, ...(refundablePolicy ? [refundablePolicy] : [])]
    return policies
      .sort((a, b) => b.policy.leadDaysMin - a.policy.leadDaysMin)
      .filter(policy => policy.policy.value !== 100 && !policy.nonRefundable)
  }, [simplifiedPolicies, refundablePolicy])

  const cancellationPolicyTitle = intl.formatMessage({
    id: 'oUa2xsT75Z',
    defaultMessage: 'Cancellation policy',
    description: 'Default title for cancellation policy modal when refund status is unknown',
  })

  const cancellationPolicyDescription = intl.formatMessage({
    id: 'ZAjDMwWJZu',
    defaultMessage: 'Things don’t always work out, so enjoy peace of mind knowing that the following cancellation options are available to you.',
    description: 'Description text in cancellation policy modal explaining available refund options',
  })

  const freeCancellationLabel = intl.formatMessage({
    id: 'RUYCf/WL8/',
    defaultMessage: 'Free cancellation',
    description: 'Label for free cancellation policy in cancellation policy modal',
  })

  const nonRefundableLabel = intl.formatMessage({
    id: 'pl0mCqz9pk',
    defaultMessage: 'Non-refundable',
    description: 'Label for non-refundable cancellation policy in cancellation policy modal',
  })

  const noCancellationFeeLabel = intl.formatMessage({
    id: 'apYBbVkuef',
    defaultMessage: 'No cancellation fee applies',
    description: 'Label when no cancellation fee applies in cancellation policy modal',
  })

  const upToCheckInLabel = intl.formatMessage({
    id: 'dd26a63ljV',
    defaultMessage: 'Up to check-in',
    description: 'Time period label for non-refundable policy when lead days is zero in cancellation policy modal',
  })

  const showModal = useContext(ModalContext)

  const openPolicyModal = useCallback<MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>>((event) => {
    event.preventDefault()
    const fromDaysUntilCheckInLabel = nonRefundablePolicy ? intl.formatMessage(
      {
        id: 'f6WHgwlQbU',
        defaultMessage: 'From {leadDaysMin, plural, one {# day} other {# days}} until check-in',
        description: 'Time period label showing days until check-in for non-refundable cancellation policy',
      },
      { leadDaysMin: nonRefundablePolicy.policy.leadDaysMin },
    ) : ''
    showModal(<Modal
      title={refundable !== null ? refundDescriptions[refundable] : cancellationPolicyTitle}
    >
      <Group direction="vertical" gap={40}>
        {refundable !== Refundable.Non && <BodyText variant="medium">
          {cancellationPolicyDescription}
        </BodyText>}
        <Group direction="vertical" gap={16}>
          <Divider kind="primary" />
          {orderedPolicies.length > 0 && orderedPolicies.map(policy => {
            const upToDaysPriorLabel = intl.formatMessage(
              {
                id: 'x7+Q2MOwel',
                defaultMessage: 'Up to {leadDaysMin, plural, one {# day} other {# days}} prior to check-in',
                description: 'Time period label showing days before check-in for cancellation policy',
              },
              { leadDaysMin: policy.policy.leadDaysMin },
            )
            const fullRefundAccommodationText = intl.formatMessage(
              {
                id: 'OeU3RVio7z',
                defaultMessage: 'Accommodation bookings can be cancelled with a full refund provided the cancellation is made no less than {leadDaysMin, plural, one {# day} other {# days}} prior to check-in.',
                description: 'Full refund cancellation policy description for accommodation bookings',
              },
              { leadDaysMin: policy.policy.leadDaysMin },
            )
            const percentageRefundLabel = intl.formatMessage(
              {
                id: 'gooRD7tJP8',
                defaultMessage: '{refundPercentage}% refund of accommodation purchase price to credit',
                description: 'Partial refund percentage label for accommodation in cancellation policy modal',
              },
              { refundPercentage: 100 - policy.policy.value },
            )
            const partialRefundAccommodationText = intl.formatMessage(
              {
                id: 'xJKn+JTjb6',
                defaultMessage: 'Accommodation bookings can be cancelled with a partial refund provided the cancellation is made no less than {leadDaysMin, plural, one {# day} other {# days}} prior to check-in.',
                description: 'Partial refund cancellation policy description for accommodation bookings',
              },
              { leadDaysMin: policy.policy.leadDaysMin },
            )
            const cancellationFeeLabel = intl.formatMessage(
              {
                id: '3t5SNzWWoZ',
                defaultMessage: 'A cancellation fee of {feeAmount} applies',
                description: 'Cancellation fee amount label in cancellation policy modal',
              },
              { feeAmount: intl.formatNumber(policy.policy.value, { style: 'currency', currency: currencyCode }) },
            )
            return <>
              <TermsGrid>
                <BodyText variant="medium" weight="bold">
                  {upToDaysPriorLabel}
                </BodyText>
                <Group direction="vertical" gap={16}>
                  {policy.policy.unit === 'percentage' && policy.policy.value === 0 && (
                    <>
                      <BodyText variant="medium">
                        {freeCancellationLabel}
                      </BodyText>
                      <BodyText variant="medium">
                        {fullRefundAccommodationText}
                      </BodyText>
                    </>
                  )}
                  {policy.policy.unit === 'percentage' && policy.policy.value !== 0 && (
                    <>
                      <BodyText variant="medium">
                        {percentageRefundLabel}
                      </BodyText>
                      <BodyText variant="medium">
                        {partialRefundAccommodationText}
                      </BodyText>
                    </>
                  )}
                  {policy.policy.unit === 'amount' && (
                    <>
                      <BodyText variant="medium">
                        {policy.policy.value === 0 ? noCancellationFeeLabel : cancellationFeeLabel}
                      </BodyText>
                      <BodyText variant="medium">
                        {policy.policy.value === 0 ? fullRefundAccommodationText : partialRefundAccommodationText}
                      </BodyText>
                    </>
                  )}
                </Group>
              </TermsGrid>
              <Divider kind="primary" />
            </>
          })}

          <TermsGrid>
            {nonRefundablePolicy && <BodyText variant="medium" weight="bold">
              {nonRefundablePolicy.policy.leadDaysMin === 0 ? upToCheckInLabel : fromDaysUntilCheckInLabel}
            </BodyText>}
            <BodyText variant="medium">
              {nonRefundableLabel}
            </BodyText>
          </TermsGrid>
        </Group>
        <HelpPane />
      </Group>
    </Modal>)
  }, [cancellationPolicyDescription, cancellationPolicyTitle, currencyCode, freeCancellationLabel, intl, noCancellationFeeLabel, nonRefundableLabel, nonRefundablePolicy, orderedPolicies, refundDescriptions, refundable, showModal, upToCheckInLabel])

  return <>
    {(refundable === Refundable.Partial || refundable === Refundable.Fully) &&
      <TextLink
        size="medium"
        colour="highlight-secondary"
        underline={false}
        startIcon={<SolidShieldCheckIcon />}
        endIcon={<LineInfoCircleIcon />}
        onClick={openPolicyModal}
      >
        {refundDescriptions[refundable]}
      </TextLink>}
    {refundable === Refundable.Non &&
      <TextLink
        size="medium"
        startIcon={<LineShieldSlashIcon />}
        endIcon={<LineInfoCircleIcon />}
        onClick={openPolicyModal}
      >{refundDescriptions[refundable]}
      </TextLink>}
  </>
}

export default StaticConnectionCancellationPoliciesComponent
